import React, { forwardRef, useState } from "react";
import { IoIosCheckmarkCircle } from "react-icons/io";
import styles from "./ModalSaveModeGroup.module.scss";
import { toast } from "react-toastify";
import mixpanel from "@/shared/lib/mixpanel";
import { useMe, useCurrentOrganization } from "@/Hooks";
import Loader from "../Loader/Loader";

interface ModalSaveModeGroupProps {
  groupName: string;
  changeGroupName: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleCreateGroup: () => Promise<void>;
  isCollection?: boolean;
  isCreating?: boolean;
}

const ModalSaveModeGroup = forwardRef<HTMLDivElement, ModalSaveModeGroupProps>(
  (
    {
      groupName,
      changeGroupName,
      handleCreateGroup,
      isCollection = false,
      isCreating = false,
    },
    ref,
  ) => {
    const [error, setError] = useState<string | null>(null);
    const { me } = useMe();
    const currentOrganization = useCurrentOrganization();

    const handleCreate = async () => {
      try {
        // Track the creation click event
        mixpanel.track(
          isCollection
            ? "Collection Create Button Clicked"
            : "Group Create Button Clicked",
          {
            [isCollection ? "collection_name" : "group_name"]: groupName,
            UserEmail: me?.email || "",
            OrganizationID: currentOrganization?.uuid || "",
            source: "ModalSaveModeGroup",
          },
        );

        await handleCreateGroup();

        // Update user profile with creation count
        if (me?.email) {
          mixpanel.identify(me.email);
          setTimeout(() => {
            mixpanel.people.increment(
              isCollection
                ? "total_created_collections"
                : "total_created_groups",
              1,
            );
          }, 100);
        }

        setError(null);
      } catch (err) {
        setError(
          `Failed to create ${isCollection ? "collection" : "group"}. Please try again.`,
        );
        toast.error(`${error}`);
      }
    };

    return (
      <div ref={ref} className={styles.root}>
        <div className={styles.groupContainer}>
          <h3>{isCollection ? "Collection name" : "Group name"}</h3>
          <input
            value={groupName}
            maxLength={35}
            onChange={changeGroupName}
            type="text"
            autoFocus
            placeholder={`Enter ${isCollection ? "collection" : "group"} name`}
          />
        </div>
        <div className={styles.buttonCreate}>
          <button onClick={handleCreate} disabled={isCreating}>
            {isCreating ? (
              <Loader color="#fff" size={20} />
            ) : (
              <span>Create</span>
            )}
          </button>
        </div>
      </div>
    );
  },
);

export default ModalSaveModeGroup;
